import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { MdAdd, MdClose, MdEdit } from "react-icons/md";
import API_BASE_URL from "../../../utils/apiBase";
import { useProjects, createProject, renameProject } from "../../../utils/projects";
import ProjectLogo from "../../../Components/ProjectLogo/ProjectLogo";
import ExportToExcel from "../../../utils/jsonToExcel";
import CalenderData from "../CalenderData";
import "./ProjectsView.css";

const today = () => new Date().toISOString().split("T")[0];

const TABS = [
  { key: "customer", label: "Customer" },
  { key: "channelPartner", label: "Channel Partner" },
  { key: "multivisit", label: "Multiple Visit" },
];

// One view for every project (site) — the original four and any the admin
// adds. Pick a project, choose a date range, and see its visitors.
// `lockedProject`: a single project's own page (opened from its sidebar
// entry) — no picker, header shows that project.
const ProjectsView = ({ searchData, handleRowClick, rowSelect, lockedProject, onRenamed }) => {
  const { projects, reload } = useProjects();
  const isAdmin = localStorage.getItem("username") === "Admin Inframantra";

  const [selected, setSelected] = useState(lockedProject || "");
  const [range, setRange] = useState({ startDate: today(), endDate: today() });
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("customer");
  const [loading, setLoading] = useState(false);

  // Dialog: null, { mode: "add" } or { mode: "rename", oldName }
  const [modal, setModal] = useState(null);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const isRename = modal?.mode === "rename";
  const openAdd = () => {
    setNewName("");
    setModal({ mode: "add" });
  };
  const openRename = (name) => {
    setNewName(name);
    setModal({ mode: "rename", oldName: name });
  };
  const canRename = (name) => isAdmin && projects.some((p) => p.name === name && !p.builtIn);

  useEffect(() => {
    if (!selected && projects.length > 0) setSelected(projects[0].name);
  }, [projects, selected]);

  const fetchData = async (project = selected) => {
    if (!project) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/allData/date`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: range, source: "project", project }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || "Could not load visitors");
      setData(body.data);
    } catch (error) {
      setData(null);
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Load straight away when a project is picked (range changes use Fetch).
  useEffect(() => {
    if (selected) fetchData(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (isRename) {
        const result = await renameProject(modal.oldName, newName);
        const { name, updatedVisitors, updatedPartners } = result.data;
        toast.success(
          `${result.message} · ${updatedVisitors} visitor${updatedVisitors === 1 ? "" : "s"} and ${updatedPartners} partner${updatedPartners === 1 ? "" : "s"} moved`
        );
        await reload();
        if (lockedProject) onRenamed?.(name);
        else setSelected(name);
      } else {
        const result = await createProject(newName);
        toast.success(result.message);
        await reload();
        setSelected(result.data.name);
      }
      setModal(null);
      setNewName("");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const view = searchData(data);
  const customers = view?.customer || [];
  const partners = view?.channelPartner || [];
  const counts = {
    customer: customers.length,
    channelPartner: partners.length,
    multivisit: customers.filter((c) => Number(c.visitCount || 0) > 1).length,
  };
  const activeLabel = TABS.find((t) => t.key === tab).label.toLowerCase();

  const onKeyActivate = (callback) => (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      callback();
    }
  };

  return (
    <div className="reservation-box projects-view">
      {lockedProject ? (
        <div className="top projects-header">
          <div className="projects-heading projects-heading--single">
            <ProjectLogo name={lockedProject} size={46} />
            <div>
              <h2>{lockedProject}</h2>
              <p>Visitors at this project</p>
            </div>
          </div>
          {canRename(lockedProject) && (
            <button type="button" className="pv-btn pv-btn--ghost" onClick={() => openRename(lockedProject)}>
              <MdEdit /> Rename
            </button>
          )}
        </div>
      ) : (
      <div className="top projects-header">
        <div className="projects-heading">
          <h2>Projects</h2>
          <p>
            {projects.length} projects · pick one to see its visitors
          </p>
        </div>
        {isAdmin && (
          <button type="button" className="pv-btn pv-btn--primary" onClick={openAdd}>
            <MdAdd /> Add project
          </button>
        )}
      </div>
      )}

      {!lockedProject && (
      <div className="projects-picker" role="listbox" aria-label="Projects">
        {projects.map((project) => (
          <div key={project.name} className="project-card-wrap">
            <button
              type="button"
              role="option"
              aria-selected={project.name === selected}
              className={`project-card${project.name === selected ? " is-selected" : ""}`}
              onClick={() => setSelected(project.name)}
            >
              <ProjectLogo name={project.name} size={40} />
              <span className="project-card-text">
                <strong>{project.name}</strong>
                <small>{project.builtIn ? "Original project" : "Added by admin"}</small>
              </span>
            </button>
            {canRename(project.name) && (
              <button
                type="button"
                className="project-card-edit"
                aria-label={`Rename ${project.name}`}
                title="Rename"
                onClick={() => openRename(project.name)}
              >
                <MdEdit />
              </button>
            )}
          </div>
        ))}
      </div>
      )}

      <div className="top">
        <div className="static">
          <div className="input-container">
            <label>From</label>
            <input
              type="date"
              className="date-field"
              value={range.startDate}
              onChange={(e) => setRange((r) => ({ ...r, startDate: e.target.value }))}
            />
          </div>
        </div>
        <div className="flex">
          <div className="input-container">
            <label>To</label>
            <input
              type="date"
              className="date-field"
              value={range.endDate}
              onChange={(e) => setRange((r) => ({ ...r, endDate: e.target.value }))}
            />
          </div>
          <div className="button-container">
            <button type="button" className="button ok" onClick={() => fetchData()} disabled={loading}>
              {loading ? "Loading…" : "Fetch Data"}
            </button>
          </div>
        </div>
      </div>

      <div className="table">
        <div className="tabs">
          <div className="tab_name">
            {TABS.map(({ key, label }) => (
              <p
                key={key}
                role="button"
                tabIndex={0}
                className={tab === key ? "is-active" : undefined}
                onClick={() => setTab(key)}
                onKeyDown={onKeyActivate(() => setTab(key))}
              >
                {label} <span className="tab-count">{counts[key]}</span>
              </p>
            ))}
          </div>
          {data && <ExportToExcel data={data} />}
        </div>

        {counts[tab] === 0 ? (
          <div className="projects-empty">
            {selected && <ProjectLogo name={selected} size={52} />}
            <p>
              <strong>No {activeLabel} records</strong>
              {selected ? ` for ${selected}` : ""} in this date range.
            </p>
            <small>Choose another range and press Fetch Data.</small>
          </div>
        ) : (
          <CalenderData
            dashData={view}
            activeTab={tab}
            handleRowClick={handleRowClick}
            rowSelect={rowSelect}
          />
        )}
      </div>

      {modal && (
        <div className="project-modal-backdrop" onClick={() => !saving && setModal(null)}>
          <form
            className="project-modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSave}
            aria-labelledby="project-modal-title"
          >
            <button
              type="button"
              className="project-modal-close"
              aria-label="Close"
              onClick={() => setModal(null)}
              disabled={saving}
            >
              <MdClose />
            </button>
            <div className="project-modal-preview">
              <ProjectLogo name={newName.trim() || "New Project"} size={68} />
            </div>
            <h3 id="project-modal-title">{isRename ? "Rename project" : "Add a project"}</h3>
            {isRename ? (
              <p>
                Everywhere <strong>{modal.oldName}</strong> appears — the forms, sidebar, charts and
                its existing visitor records — will use the new name.
              </p>
            ) : (
              <p>
                It appears in the Place of Visit list on the Customer and Channel Partner forms,
                alongside Sovereign Park and Vatika Seven Elements.
              </p>
            )}
            <label htmlFor="project-name-input">Project name</label>
            <input
              id="project-name-input"
              autoFocus
              value={newName}
              maxLength={60}
              placeholder="e.g. Godrej Aristocrat"
              onChange={(e) => setNewName(e.target.value)}
            />
            <div className="project-modal-actions">
              <button
                type="button"
                className="pv-btn pv-btn--ghost"
                onClick={() => setModal(null)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="pv-btn pv-btn--primary"
                disabled={
                  saving ||
                  newName.trim().length < 2 ||
                  (isRename && newName.trim() === modal.oldName)
                }
              >
                {saving ? (isRename ? "Saving…" : "Adding…") : isRename ? "Save name" : "Add project"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ProjectsView;
